import { 
  doc, 
  setDoc, 
  onSnapshot,
  Timestamp,
  updateDoc
} from 'firebase/firestore';
import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { FIRESTORE_LOCATIONS_COLLECTION } from '../constants/firestoreCollectionSecurity';
import { MigrationDataAccessLayer } from './migrationDataAccessLayer';
import {
  refreshLocationVisibilityForCarrier,
  scheduleLocationVisibilityRefresh,
} from './locationVisibilityService';
import Logger from './logger';

/**
 * Validate coordinate values
 */
const isValidCoordinate = (lng: number, lat: number): boolean => {
  // Check if coordinates are valid numbers
  if (typeof lng !== 'number' || typeof lat !== 'number') {
    return false;
  }
  
  // Check if coordinates are within valid ranges
  if (isNaN(lng) || isNaN(lat)) {
    return false;
  }
  
  // Longitude: -180 to 180
  if (lng < -180 || lng > 180) {
    return false;
  }
  
  // Latitude: -90 to 90
  if (lat < -90 || lat > 90) {
    return false;
  }
  
  return true;
};

interface LocationUpdate {
  userId: string;
  position: [number, number]; // [longitude, latitude]
  heading?: number;
  speed?: number;
  accuracy?: number;
  timestamp: Timestamp;
  status: 'online' | 'offline' | 'inactive';
  lastHeartbeat: Timestamp;
}

export const locationService = {
  // Start tracking a user's location
  startTracking: (userId: string) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      throw new Error('Geolocation is not supported by this browser.');
    }

    void refreshLocationVisibilityForCarrier(userId).catch((err) => {
      Logger.warn('[LocationService] Initial visibility refresh failed:', err as Error);
    });

    // Watch position and update Firestore
    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        try {
          const lng = position.coords.longitude;
          const lat = position.coords.latitude;

          // Validate coordinates before updating
          if (!isValidCoordinate(lng, lat)) {
            Logger.warn(`[LocationService] Invalid coordinates for user ${userId}:`, { lng, lat });
            return;
          }

          // Only include fields if they are defined
          const locationUpdate: any = {
            userId,
            position: [lng, lat],
            accuracy: position.coords.accuracy,
            timestamp: Timestamp.now(),
            status: 'online',
            lastHeartbeat: Timestamp.now()
          };
          if (typeof position.coords.heading === 'number') locationUpdate.heading = position.coords.heading;
          if (typeof position.coords.speed === 'number') locationUpdate.speed = position.coords.speed;

          const db = getFailoverAwareDatabase();
          const locationRef = doc(db, FIRESTORE_LOCATIONS_COLLECTION, userId);
          await setDoc(locationRef, locationUpdate, { merge: true });

          Logger.debug(`[LocationService] Location updated for user ${userId}:`, {
            coordinates: [lng, lat],
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          });

          // Also update the user's online status + live map coords (Available Carriers SSoT)
          const migrationDataAccess = new MigrationDataAccessLayer();
          const coords: [number, number] = [position.coords.longitude, position.coords.latitude];
          await migrationDataAccess.updateUserProfile(userId, {
            status: 'online',
            location: coords,
            lastLocation: coords,
            lastHeartbeat: Timestamp.now(),
            lastActive: Timestamp.now(),
          });
        } catch (error) {
          Logger.error('Error updating location:', error as Error);
        }
      },
      (error) => {
        Logger.error('Error getting location:', new Error(`Geolocation error: ${error.message || 'Unknown error'}`), { code: error.code, message: error.message });
      },
      {
        enableHighAccuracy: true,
        timeout: 15000, // Increased from 10000 to 15000 for better reliability
        maximumAge: 0 // Force fresh location - don't use cached location
      }
    );

    // Start heartbeat to maintain online status + refresh active-leg visibility
    const heartbeatInterval = setInterval(async () => {
      try {
        const db = getFailoverAwareDatabase();
        const locationRef = doc(db, FIRESTORE_LOCATIONS_COLLECTION, userId);
        await updateDoc(locationRef, {
          lastHeartbeat: Timestamp.now()
        });
        scheduleLocationVisibilityRefresh(userId);
        // Keep companyUserLive presence fresh while session is active (coords from last GPS fix)
        const migrationDataAccess = new MigrationDataAccessLayer();
        await migrationDataAccess.updateUserProfile(userId, {
          status: 'online',
          lastHeartbeat: Timestamp.now(),
          lastActive: Timestamp.now(),
        });
      } catch (error) {
        Logger.error('Error updating heartbeat:', error as Error);
      }
    }, 30000); // Every 30 seconds

    return () => {
      // Cleanup function
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
      clearInterval(heartbeatInterval);
    };
  },

  // Stop tracking and mark as offline
  stopTracking: async (userId: string) => {
    try {
      const db = getFailoverAwareDatabase();
      const locationRef = doc(db, FIRESTORE_LOCATIONS_COLLECTION, userId);
      await updateDoc(locationRef, {
        status: 'offline',
        lastHeartbeat: Timestamp.now()
      });
      // Clear sticky Available Carriers presence (was leaving status online forever)
      const migrationDataAccess = new MigrationDataAccessLayer();
      await migrationDataAccess.updateUserProfile(userId, {
        status: 'offline',
        lastHeartbeat: Timestamp.now(),
        lastActive: Timestamp.now(),
      });
      scheduleLocationVisibilityRefresh(userId);
    } catch (error) {
      Logger.error('Error stopping tracking:', error as Error);
    }
  },

  // Subscribe to location updates for specific users (per-doc — rules-safe under C1)
  subscribeToLocations: (userIds: string[], onUpdate: (locations: Record<string, LocationUpdate>) => void) => {
    const db = getFailoverAwareDatabase();
    const locations: Record<string, LocationUpdate> = {};
    const unsubscribers: Array<() => void> = [];

    const emitUpdate = () => onUpdate({ ...locations });

    userIds.forEach((userId) => {
      const locationRef = doc(db, FIRESTORE_LOCATIONS_COLLECTION, userId);
      const unsubscribe = onSnapshot(
        locationRef,
        (snap) => {
          if (!snap.exists()) {
            delete locations[userId];
            emitUpdate();
            return;
          }

          const data = snap.data() as LocationUpdate;
          const now = Timestamp.now();
          const staleThreshold = 2 * 60; // 2 minutes in seconds

          if (data.lastHeartbeat && (now.seconds - data.lastHeartbeat.seconds) > staleThreshold) {
            data.status = 'inactive';
          }

          locations[userId] = data;
          emitUpdate();
        },
        (error) => {
          Logger.error(`Error subscribing to location for ${userId}:`, error as Error);
        }
      );
      unsubscribers.push(unsubscribe);
    });

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }
};
